'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useDebounce } from './use-debounce'
import type { ApiResponse, PaginatedData } from '@/shared/schemas/api-schema'

export type SortDirection = "asc" | "desc"

export interface PaginationParams {
  pageNumber?: number
  pageSize?: number
  sortBy?: string
  sortDirection?: SortDirection
}

export type ResourceFilters = Record<string, any>

export interface UsePaginatedResourceOptions<TData, TFilters extends ResourceFilters = ResourceFilters> {
  // Resource identifier for caching
  resourceId: string

  // Function to fetch data - must return the standardized API response
  fetchFn: (params: PaginationParams & TFilters) => Promise<ApiResponse<PaginatedData<TData>>>

  // Initial pagination values
  initialPage?: number
  initialPageSize?: number
  initialSortColumn?: string
  initialSortDirection?: SortDirection

  // Initial filters
  initialFilters?: Partial<TFilters>

  // Search configuration
  searchField?: keyof TFilters
  searchDebounceMs?: number

  // Whether to fetch data on mount
  fetchOnMount?: boolean

  // Custom enabled condition for the query
  enabled?: boolean

  // Disable react-query cache for this resource
  disableCache?: boolean

  // Cache configuration (React Query options)
  staleTime?: number // Time before data is considered stale (default: 5 minutes)
  gcTime?: number // Time before unused data is garbage collected (default: 10 minutes)
  refetchOnMount?: boolean | 'always'

  // Whether to keep internal filters in sync with changes to initialFilters
  // Default true to preserve existing behavior; can be disabled for Apply-only flows
  syncInitialFilters?: boolean
}

/**
 * A hook for fetching paginated resources with standardized API responses
 * 
 * @deprecated Use feature-specific query hooks with TanStack Query instead.
 * This generic hook is being phased out in favor of the three-layer architecture:
 * - API Layer: Pure API call functions
 * - Query Layer: Query keys, queryOptions, and mutationOptions
 * - Hook Layer: Custom hooks wrapping TanStack Query
 * 
 * For migration guidance, see: docs/architecture/data-fetching.md
 * 
 * Example migration:
 * ```typescript
 * // Old approach (deprecated):
 * const { data, isLoading } = usePaginatedResource({
 *   resourceId: 'users',
 *   fetchFn: usersApi.getUsers,
 *   ...
 * })
 * 
 * // New approach:
 * // 1. Create query service: features/users/services/users.query.ts
 * // 2. Create custom hook: features/users/hooks/use-users.ts
 * // 3. Use in component:
 * const { data, isLoading } = useUsers(params)
 * ```
 */
export function usePaginatedResource<TData, TFilters extends ResourceFilters = ResourceFilters>(
  options: UsePaginatedResourceOptions<TData, TFilters>,
) {
  const {
    resourceId,
    fetchFn,
    initialPage = 1,
    initialPageSize = 10,
    initialSortColumn,
    initialSortDirection = "asc",
    initialFilters = {} as Partial<TFilters>,
    searchField,
    searchDebounceMs = 300,
    fetchOnMount = true,
    enabled,
    disableCache = false,
    staleTime = 1000 * 60 * 5, // Default: 5 minutes
    gcTime = 1000 * 60 * 10, // Default: 10 minutes
    refetchOnMount: refetchOnMountOption,
  } = options

  const [page, setPage] = useState(initialPage)
  const [pageSize, setPageSize] = useState(initialPageSize)

  const [sort, setSort] = useState({
    column: initialSortColumn || null,
    direction: initialSortDirection,
  })

  const [filters, setFilters] = useState<Partial<TFilters>>(initialFilters)
  const prevFiltersRef = useRef<string>(JSON.stringify(initialFilters))
  const isFirstFiltersEffect = useRef(true)
  // Keep internal filters in sync if requested
  useEffect(() => {
    if (options.syncInitialFilters) {
      setFilters(initialFilters)
    }
  }, [options.syncInitialFilters, JSON.stringify(initialFilters)])

  const [searchInput, setSearchInput] = useState<string>(
    searchField ? (initialFilters[searchField] as string) || "" : "",
  )
  const debouncedSearch = useDebounce(searchInput, searchDebounceMs)

  useEffect(() => {
    if (searchField) {
      setFilters((prev) => ({
        ...prev,
        [searchField]: debouncedSearch || undefined,
      }))
    }
  }, [debouncedSearch, searchField])

  useEffect(() => {
    const serialized = JSON.stringify(filters)
    const prevSerialized = prevFiltersRef.current

    // Skip the first run to preserve initialPage from URL/state
    if (isFirstFiltersEffect.current) {
      isFirstFiltersEffect.current = false
      prevFiltersRef.current = serialized
      return
    }

    if (serialized !== prevSerialized) {
      setPage(1)
      prevFiltersRef.current = serialized
    }
  }, [filters])

  const queryParams = {
    pageNumber: page,
    pageSize,
    sortBy: sort.column || undefined,
    sortDirection: sort.column ? sort.direction : undefined,
    ...filters,
  } as PaginationParams & TFilters

  const queryKey = [
    resourceId,
    page,
    pageSize,
    sort.column,
    sort.direction,
    JSON.stringify(filters)
  ]

  const queryResult = useQuery({
    queryKey,
    queryFn: async () => {
      return await fetchFn(queryParams)
    },
    enabled: enabled !== undefined ? enabled : fetchOnMount,
    // Cache configuration
    staleTime: disableCache ? 0 : staleTime,
    gcTime: disableCache ? 0 : gcTime,
    refetchOnMount:
      refetchOnMountOption ?? (disableCache ? 'always' : true),
    refetchOnWindowFocus: disableCache ? true : false,
    // Custom retry logic for permission errors
    retry: (failureCount, error: any) => {
      // Don't retry permission errors
      if (error?.response?.statusCode === 403 || 
          error?.response?.errorCode === 'FORBIDDEN' ||
          error?.statusCode === 403 ||
          error?.message?.toLowerCase().includes('forbidden') ||
          error?.message?.toLowerCase().includes('access denied') ||
          error?.message?.toLowerCase().includes('insufficient permissions')) {
        console.log('usePaginatedResource: Preventing retry for permission error')
        return false
      }
      // Only retry once for other errors
      return failureCount < 1
    },
  })

  const data = queryResult.data?.data?.items || []
  const totalCount = queryResult.data?.data?.totalCount || 0
  const totalPages = Math.ceil(totalCount / pageSize)

  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages)
    }
  }, [page, totalPages])

  const handleSortClick = useCallback((column: string) => {
    setSort((prev) => {
      if (prev.column !== column) {
        return {
          column,
          direction: "asc" as const,
        }
      }
      
      if (prev.direction === "asc") {
        return {
          column,
          direction: "desc" as const,
        }
      } else if (prev.direction === "desc") {
        return {
          column: null,
          direction: "asc" as const,
        }
      } else {
        return {
          column,
          direction: "asc" as const,
        }
      }
    })
    setPage(1)
  }, [])

  // Update filters
  const updateFilters = useCallback((newFilters: Partial<TFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }))
    // Page reset is handled by the useEffect
  }, [])

  // Reset filters
  const resetFilters = useCallback(() => {
    setFilters(initialFilters)
    if (searchField) {
      setSearchInput((initialFilters[searchField] as string) || "")
    }
    // Page reset is handled by the useEffect
  }, [initialFilters, searchField])

  // Handle page change
  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage)
  }, [])

  // Handle page size change
  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize)
    setPage(1) // Reset to first page when page size changes
  }, [])

  // Set sort directly (useful for programmatic sorting)
  const setSortDirect = useCallback((column: string | null, direction: SortDirection = "asc") => {
    setSort({ column, direction })
    setPage(1)
  }, [])

  return {
    // Data
    data,
    totalCount,
    totalPages,

    // Pagination
    page,
    pageSize,
    setPage: handlePageChange,
    setPageSize: handlePageSizeChange,

    // Sorting
    sort,
    handleSortClick,
    setSort: setSortDirect,

    // Filters
    filters,
    updateFilters,
    resetFilters,

    // Search (if searchField is provided)
    searchInput: searchField ? searchInput : undefined,
    setSearchInput: searchField ? setSearchInput : undefined,

    // Query state
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,

    // Raw query result for advanced use cases
    queryResult,
  }
}
